import type { ClientDTO } from '@copilot/shared';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { clientsApi } from '../api/clients';
import { Input } from './Input';
import { Spinner } from './Spinner';

/** Debounce a changing value. */
export function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** Search-as-you-type client chooser (GET /v1/clients?q=). */
export function ClientPicker({
  onSelect,
  excludeId,
  placeholder = 'Search clients…',
  autoFocus,
}: {
  onSelect: (client: ClientDTO) => void;
  excludeId?: string;
  placeholder?: string;
  autoFocus?: boolean;
}) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const debounced = useDebounced(q.trim());
  const query = useQuery({
    queryKey: ['clients', 'picker', debounced],
    queryFn: () => clientsApi.list({ q: debounced || undefined, pageSize: 10 }),
    enabled: open,
  });
  const items = (query.data?.items ?? []).filter((c) => c.id !== excludeId);

  return (
    <div className="relative">
      <Input
        value={q}
        placeholder={placeholder}
        autoFocus={autoFocus}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        aria-label="Search clients"
      />
      {open && (
        <div className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-line bg-surface shadow-lg">
          {query.isLoading ? (
            <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted">
              <Spinner size="sm" /> Searching…
            </div>
          ) : query.isError ? (
            <div className="px-3 py-2 text-sm text-danger">Could not load clients</div>
          ) : items.length === 0 ? (
            <div className="px-3 py-2 text-sm text-muted">No clients found</div>
          ) : (
            items.map((c) => (
              <button
                key={c.id}
                type="button"
                className="block w-full px-3 py-1.5 text-left text-sm hover:bg-subtle"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onSelect(c);
                  setQ('');
                  setOpen(false);
                }}
              >
                <span className="text-ink">{c.name}</span>
                {c.domains.length > 0 && <span className="ml-2 text-xs text-muted">{c.domains.join(', ')}</span>}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
